import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, cartTotal, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="min-h-screen max-w-3xl mx-auto px-4 sm:px-8 pt-[118px] sm:pt-[120px] lg:pt-[122px] pb-16 text-center">
        <ShoppingBag className="h-16 w-16 mx-auto text-[#78716C] mb-6" />
        <h1 className="font-serif text-3xl mb-4">Your cart is empty</h1>
        <p className="text-[#78716C] mb-8">Discover our handpicked collection</p>
        <Link
          to="/shop"
          data-testid="empty-cart-shop-btn"
          className="inline-block bg-[#7C1F30] hover:bg-[#8D2A3D] text-white px-8 py-4 rounded-full uppercase text-xs tracking-[0.2em] transition-colors"
        >
          Shop Now
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-w-7xl mx-auto px-4 sm:px-8 pt-[110px] sm:pt-[112px] lg:pt-[122px] pb-12 lg:pb-16">
      <h1 className="font-serif text-4xl sm:text-5xl text-[#1C1917] mb-10">Your Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div
              key={`${item.product_id}-${item.variant_sku || "base"}`}
              data-testid={`cart-item-${item.product_id}`}
              className="flex gap-4 p-4 bg-white border border-[#E7E5E4] rounded-lg"
            >
              <img
                src={item.product.image}
                alt={item.product.name}
                className="w-24 h-32 object-cover rounded-md"
              />
              <div className="flex-1">
                <h3 className="font-serif text-lg mb-1">{item.product.name}</h3>
                <p className="text-xs text-[#78716C] mb-2 capitalize">
                  {(item.product.category || "").replace("-", " ")}
                </p>
                <p className={`mb-2 text-xs ${
                  item.checkout_eligible ? "text-green-700" : "text-red-700"
                }`}>
                  {item.availability_message || "Checking stock availability"}
                </p>
                {item.variant && (
                  <div className="flex items-center gap-2 mb-3 text-xs">
                    {item.variant.color && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#F5F5F4] rounded-full">
                        {item.variant.color_hex && (
                          <span className="w-2.5 h-2.5 rounded-full ring-1 ring-white" style={{ background: item.variant.color_hex }} />
                        )}
                        {item.variant.color}
                      </span>
                    )}
                    {item.variant.size && (
                      <span className="px-2 py-0.5 bg-[#F5F5F4] rounded-full">Size {item.variant.size}</span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-[#E7E5E4] rounded-full">
                    <button
                      onClick={() =>
                        updateQuantity(item.product_id, Math.max(1, item.quantity - 1), item.variant_sku)
                      }
                      data-testid={`cart-dec-${item.product_id}`}
                      className="p-2 hover:bg-[#F5F5F4] rounded-l-full"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="px-3 text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.variant_sku)}
                      disabled={
                        !item.available_quantity ||
                        item.quantity >= item.available_quantity
                      }
                      data-testid={`cart-inc-${item.product_id}`}
                      className="p-2 hover:bg-[#F5F5F4] rounded-r-full"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.product_id, item.variant_sku)}
                    data-testid={`cart-remove-${item.product_id}`}
                    className="text-[#78716C] hover:text-[#7C1F30] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium text-[#1C1917]">
                  {item.checkout_eligible
                    ? formatINR(item.product.price * item.quantity)
                    : "Not included"}
                </div>
                <div className="text-xs text-[#78716C] mt-1">
                  {formatINR(item.product.price)} each
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <aside className="lg:col-span-1">
          <div className="bg-white border border-[#E7E5E4] rounded-lg p-6 sticky top-28" data-testid="cart-summary">
            <h2 className="font-serif text-xl mb-6">Order Summary</h2>
            <div className="space-y-3 text-sm mb-6">
              <div className="flex justify-between">
                <span className="text-[#78716C]">Subtotal</span>
                <span data-testid="cart-subtotal">{formatINR(cartTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">Shipping</span>
                <span>{cartTotal >= 1499 ? "Free" : "Calculated at checkout"}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-[#E7E5E4] font-medium text-base">
                <span>Total</span>
                <span data-testid="cart-total">
                  {formatINR(cartTotal)}
                </span>
              </div>
            </div>
            <Button
              onClick={() => navigate("/checkout")}
              disabled={!items.some((item) => item.checkout_eligible === true)}
              data-testid="checkout-btn"
              className="w-full bg-[#7C1F30] hover:bg-[#8D2A3D] text-white rounded-full py-6 uppercase text-xs tracking-[0.2em]"
            >
              Proceed to Checkout
            </Button>
            <Link
              to="/shop"
              className="block text-center text-sm text-[#78716C] hover:text-[#7C1F30] mt-4"
            >
              Continue shopping
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
